import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const ActionButtonOverlay = () => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showQuickEntry, setShowQuickEntry] = useState(false);

  const shouldShowFAB = ['/daily-check-in', '/symptom-history']?.includes(location?.pathname);

  const quickActions = [
    {
      icon: 'Users',
      label: 'Manage Dependents',
      path: '/user-relationship-management',
      color: 'bg-primary',
      roles: ['caregiver']
    },
    {
      icon: 'BarChart3',
      label: 'View Analytics',
      path: '/caregiver-analytics-dashboard',
      color: 'bg-accent',
      roles: ['caregiver']
    },
    {
      icon: 'Shield',
      label: 'Audit Trail',
      path: '/audit-trail-dashboard',
      color: 'bg-success',
      roles: ['caregiver']
    },
    {
      icon: 'Bell',
      label: 'Notifications',
      path: '/notification-management',
      color: 'bg-warning',
      roles: ['caregiver']
    },
    {
      icon: 'Plus',
      label: 'Daily Check-in',
      path: '/daily-check-in',
      color: 'bg-primary',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    },
    {
      icon: 'Clock',
      label: 'Symptom History',
      path: '/symptom-history',
      color: 'bg-accent',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    },
    {
      icon: 'FileText',
      label: 'Export Summary',
      path: '/doctor-summary-export',
      color: 'bg-success',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    }
  ];

  useEffect(() => {
    if (!shouldShowFAB) {
      setIsExpanded(false);
      setShowQuickEntry(false);
    }
  }, [location?.pathname, shouldShowFAB]);

  const handleMainButtonClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleVoiceNoteClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices?.getUserMedia({ audio: true });
        setIsRecording(true);
        console.log('Voice recording started');
        
        setTimeout(() => {
          stream?.getTracks()?.forEach(track => track?.stop());
          setIsRecording(false);
          setIsExpanded(false);
          console.log('Voice recording stopped');
        }, 5000);
      } catch (error) {
        console.error('Microphone access denied:', error);
        alert('Please allow microphone access to record voice notes');
      }
    } else {
      setIsRecording(false);
      setIsExpanded(false);
    }
  };

  const handleQuickEntryClick = () => {
    setShowQuickEntry(true);
    setIsExpanded(false);
  };

  const handleQuickEntryClose = () => {
    setShowQuickEntry(false);
  };

  const handleQuickEntrySubmit = (e) => {
    e?.preventDefault();
    console.log('Quick symptom entry submitted');
    setShowQuickEntry(false);
  };

  if (!shouldShowFAB) {
    return null;
  }

  return (
    <>
      <div 
        className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-[110]"
        role="group"
        aria-label="Quick action buttons"
      >
        {isExpanded && (
          <div className="flex flex-col gap-3 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
            <button
              onClick={handleVoiceNoteClick}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-xl shadow-therapeutic-lg
                transition-smooth
                ${isRecording 
                  ? 'bg-error text-error-foreground' 
                  : 'bg-card text-foreground hover:bg-muted'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              `}
              aria-label={isRecording ? 'Stop voice recording' : 'Start voice recording'}
            >
              <Icon 
                name={isRecording ? 'MicOff' : 'Mic'} 
                size={20} 
                strokeWidth={2}
              />
              <span className="text-sm font-medium whitespace-nowrap">
                {isRecording ? 'Recording...' : 'Voice Note'}
              </span>
            </button>

            <button
              onClick={handleQuickEntryClick}
              className="
                flex items-center gap-3 px-4 py-3 rounded-xl bg-card text-foreground
                shadow-therapeutic-lg hover:bg-muted transition-smooth
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              "
              aria-label="Quick symptom entry"
            >
              <Icon name="Plus" size={20} strokeWidth={2} />
              <span className="text-sm font-medium whitespace-nowrap">Quick Entry</span>
            </button>
          </div>
        )}

        <button
          onClick={handleMainButtonClick}
          className={`
            flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full
            shadow-therapeutic-xl transition-smooth
            ${isExpanded 
              ? 'bg-muted text-foreground rotate-45' 
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }
            focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
            active:scale-[0.97]
          `}
          aria-label={isExpanded ? 'Close quick actions' : 'Open quick actions'}
          aria-expanded={isExpanded}
        >
          <Icon name="Plus" size={28} strokeWidth={2.5} />
        </button>
      </div>
      {showQuickEntry && (
        <div 
          className="fixed inset-0 bg-background/95 z-[200] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-entry-title"
          onClick={handleQuickEntryClose}
        >
          <div 
            className="w-full max-w-md bg-card rounded-xl shadow-therapeutic-xl p-6 sm:p-8"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 id="quick-entry-title" className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                Quick Symptom Entry
              </h2>
              <button
                onClick={handleQuickEntryClose}
                className="
                  flex items-center justify-center w-10 h-10 rounded-lg
                  text-muted-foreground hover:text-foreground hover:bg-muted
                  transition-smooth
                  focus:outline-none focus:ring-3 focus:ring-ring
                "
                aria-label="Close quick entry"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <form onSubmit={handleQuickEntrySubmit} className="space-y-4">
              <div>
                <label htmlFor="symptom-name" className="block text-sm font-medium text-foreground mb-2">
                  Symptom Name
                </label>
                <input
                  id="symptom-name"
                  type="text"
                  placeholder="e.g., Headache, Fever"
                  className="
                    w-full px-4 py-3 rounded-lg border border-border bg-background
                    text-foreground placeholder:text-muted-foreground
                    focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                    transition-smooth
                  "
                  required
                />
              </div>

              <div>
                <label htmlFor="severity" className="block text-sm font-medium text-foreground mb-2">
                  Severity (1-10)
                </label>
                <input
                  id="severity"
                  type="range"
                  min="1"
                  max="10"
                  defaultValue="5"
                  className="w-full accent-primary"
                />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  rows="3"
                  placeholder="Additional details..."
                  className="
                    w-full px-4 py-3 rounded-lg border border-border bg-background
                    text-foreground placeholder:text-muted-foreground
                    focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                    transition-smooth resize-none
                  "
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleQuickEntryClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  iconName="Check"
                  iconPosition="right"
                  className="flex-1"
                >
                  Save Entry
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionButtonOverlay;